import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { type EventOptions } from '@/types';
import {
  type EventsInitialState,
  type SetEventsPayload,
  type SetPaginationPayload,
  type ResetEventsPayload,
  type ResetAllEventsForPropertyPayload,
  type SetCurrentEventPayload,
  type SetCurrentMatchScorePayload,
  type AddIncidentPayload,
  type UpdateIncidentPayload,
  type RemoveIncidentPayload,
} from './types';

const initialState: EventsInitialState = {
  events: {},
  pagination: {},
  eventTypeOptions: [],
  eventStatusOptions: [],
  sportOptions: [],
  currentEvent: null,
  currentIncidents: [],
  currentContributors: [],
  currentMatchScore: null,
};

export const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    setEvents: (
      state,
      {
        payload: { propertyId, page, events },
      }: PayloadAction<SetEventsPayload>,
    ) => {
      state.events[propertyId] ||= {};
      state.events[propertyId][page] = events;
    },
    setPagination: (
      state,
      {
        payload: { propertyId, pagination },
      }: PayloadAction<SetPaginationPayload>,
    ) => {
      state.pagination[propertyId] = pagination;
    },
    setEventOptions: (
      state,
      {
        payload: { eventTypes, eventStatuses, sports },
      }: PayloadAction<EventOptions>,
    ) => {
      state.eventTypeOptions = eventTypes;
      state.eventStatusOptions = eventStatuses;
      state.sportOptions = sports;
    },
    setCurrentEvent: (
      state,
      {
        payload: { event, incidents, contributors },
      }: PayloadAction<SetCurrentEventPayload>,
    ) => {
      state.currentEvent = event;
      state.currentContributors = contributors;

      if (incidents) {
        state.currentIncidents = incidents;
      }
    },
    setCurrentMatchScore: (
      state,
      { payload }: PayloadAction<SetCurrentMatchScorePayload>,
    ) => {
      state.currentMatchScore = payload;
    },
    resetEvents: (
      state,
      { payload: { page, propertyId } }: PayloadAction<ResetEventsPayload>,
    ) => {
      const events = state?.events?.[propertyId];

      state.events[propertyId] = {
        [page]: events?.[page],
      };
    },
    resetAllEventsForProperty: (
      state,
      {
        payload: { propertyId },
      }: PayloadAction<ResetAllEventsForPropertyPayload>,
    ) => {
      state.events[propertyId] = {};
      state.pagination[propertyId] = null;
    },
    resetCurrentEvent: (state) => {
      state.currentEvent = null;
      state.currentIncidents = [];
      state.currentContributors = [];
      state.currentMatchScore = null;
    },
    addIncident: (
      state,
      { payload: { incident } }: PayloadAction<AddIncidentPayload>,
    ) => {
      state.currentIncidents.unshift(incident);
    },
    updateIncident: (
      state,
      { payload: { incident } }: PayloadAction<UpdateIncidentPayload>,
    ) => {
      const index = state.currentIncidents.findIndex(
        (currentIncident) => currentIncident.id === incident.id,
      );

      if (index !== -1) {
        state.currentIncidents[index] = {
          ...state.currentIncidents[index],
          ...incident,
        };
      }
    },
    removeIncident: (
      state,
      { payload: { incidentId } }: PayloadAction<RemoveIncidentPayload>,
    ) => {
      state.currentIncidents = state.currentIncidents.filter(
        (incident) => incident.id !== incidentId,
      );
    },
  },
});

export const { reducer: eventsReducer } = eventsSlice;
